import { useAutoSaveForm } from '../../../components/forms/useAutoSaveForm'
import FormHeader from '../../../components/forms/FormHeader'
import { api } from '../../../lib/api'
import Input from '../../../components/ui/Input'
import Button from '../../../components/ui/Button'

interface State {
  home_team: string
  away_team: string
  home_score: number
  away_score: number
  minute: number
  half: 'first' | 'half_time' | 'second' | 'extra' | 'finished'
  status: 'scheduled' | 'live' | 'half_time' | 'finished'
  show: boolean
}

interface Props {
  overlayId: string
  initial: Record<string, unknown>
}

const HALF_LABELS: Record<State['half'], string> = {
  first: '1. Yarı',
  half_time: 'Devre arası',
  second: '2. Yarı',
  extra: 'Uzatma',
  finished: 'Bitti',
}

const STATUS_LABELS: Record<State['status'], string> = {
  scheduled: 'Başlamadı',
  live: 'Canlı',
  half_time: 'Devre arası',
  finished: 'Bitti',
}

export default function FootballScoreForm({ overlayId, initial }: Props) {
  const form = useAutoSaveForm<State>({
    initial: {
      home_team: (initial.home_team as string) || 'Ev Sahibi',
      away_team: (initial.away_team as string) || 'Deplasman',
      home_score: Number(initial.home_score) || 0,
      away_score: Number(initial.away_score) || 0,
      minute: Number(initial.minute) || 0,
      half: ((initial.half as State['half']) || 'first'),
      status: ((initial.status as State['status']) || 'scheduled'),
      show: initial.show !== false,
    },
    onSave: async (s) => {
      await api(`/api/overlays/${overlayId}/state`, {
        method: 'PATCH',
        body: JSON.stringify(s),
      })
    },
  })

  function bumpScore(side: 'home' | 'away', delta: number) {
    const key = side === 'home' ? 'home_score' : 'away_score'
    const next = Math.max(0, form.draft[key] + delta)
    form.patch({ [key]: next } as Partial<State>)
  }

  return (
    <div className="space-y-5">
      <FormHeader
        title="Canlı Skor (Futbol)"
        subtitle="Manuel skor güncelleme"
        status={form.status}
        savedAt={form.savedAt}
        error={form.error}
        onUndo={form.canUndo ? form.undo : undefined}
      />

      {/* Skorboard — büyük layout */}
      <section className="rounded-md border border-[var(--color-border)] bg-[var(--color-elevated)] p-4">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <div className="space-y-2">
            <label className="block text-xs font-medium text-[var(--color-text-muted)]">Ev Sahibi</label>
            <Input
              value={form.draft.home_team}
              onChange={(e) => form.patch({ home_team: e.target.value })}
              className="text-center font-semibold"
            />
            <div className="flex items-center justify-center gap-1.5">
              <Button variant="ghost" size="sm" onClick={() => bumpScore('home', -1)}>
                −
              </Button>
              <div className="text-3xl font-bold tabular-nums w-12 text-center">
                {form.draft.home_score}
              </div>
              <Button variant="ghost" size="sm" onClick={() => bumpScore('home', 1)}>
                +
              </Button>
            </div>
          </div>

          <div className="text-2xl font-bold text-[var(--color-text-muted)] px-2">:</div>

          <div className="space-y-2">
            <label className="block text-xs font-medium text-[var(--color-text-muted)]">Deplasman</label>
            <Input
              value={form.draft.away_team}
              onChange={(e) => form.patch({ away_team: e.target.value })}
              className="text-center font-semibold"
            />
            <div className="flex items-center justify-center gap-1.5">
              <Button variant="ghost" size="sm" onClick={() => bumpScore('away', -1)}>
                −
              </Button>
              <div className="text-3xl font-bold tabular-nums w-12 text-center">
                {form.draft.away_score}
              </div>
              <Button variant="ghost" size="sm" onClick={() => bumpScore('away', 1)}>
                +
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-md border border-[var(--color-border)] bg-[var(--color-elevated)] p-4 space-y-3">
        <h3 className="text-sm font-semibold">Maç Durumu</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1.5">Dakika</label>
            <Input
              type="number"
              min={0}
              max={130}
              value={form.draft.minute}
              onChange={(e) => form.patch({ minute: Number(e.target.value) })}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1.5">Devre</label>
            <select
              value={form.draft.half}
              onChange={(e) => form.patch({ half: e.target.value as State['half'] })}
              className="w-full h-9 px-3 rounded-md text-sm bg-[var(--color-elevated)] border border-[var(--color-border)] text-[var(--color-text)] hover:border-[var(--color-border-strong)] focus:border-[var(--color-accent)] focus:outline-none"
            >
              {(Object.keys(HALF_LABELS) as State['half'][]).map((k) => (
                <option key={k} value={k}>
                  {HALF_LABELS[k]}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1.5">Genel Durum</label>
          <select
            value={form.draft.status}
            onChange={(e) => form.patch({ status: e.target.value as State['status'] })}
            className="w-full h-9 px-3 rounded-md text-sm bg-[var(--color-elevated)] border border-[var(--color-border)] text-[var(--color-text)] hover:border-[var(--color-border-strong)] focus:border-[var(--color-accent)] focus:outline-none"
          >
            {(Object.keys(STATUS_LABELS) as State['status'][]).map((k) => (
              <option key={k} value={k}>
                {STATUS_LABELS[k]}
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={form.draft.show}
            onChange={(e) => form.patch({ show: e.target.checked })}
            className="accent-[var(--color-accent)]"
          />
          Yayında göster
        </label>
      </section>
    </div>
  )
}
